import { useEffect, useState } from "react";
import api from "../lib/api";

/**
 * CookieConsent — Phase 8.37
 *
 * Modern slide-up banner with three primary actions: Accept all / Reject all
 * / Cookie settings. The settings expand a granular modal where each
 * category (Necessary, Analytics, Marketing) has its own toggle.
 *
 * Storage strategy:
 *   - localStorage key `oodel_cookie_consent` = { choice, categories,
 *     timestamp, version }. Suppresses the banner on subsequent visits.
 *   - POST /api/cookies/consent fires on every click for the audit trail
 *     (server stores hashed IP + hashed UA + anon_id, never raw PII).
 *
 * The banner reappears whenever the policy version bumps (legally required
 * after material changes to data practices) or the user clears their
 * browser storage.
 */
const STORAGE_KEY = "oodel_cookie_consent";
const POLICY_VERSION = "v1";
const ANON_ID_KEY = "oodel_anon_id";

function getAnonId() {
  let id = localStorage.getItem(ANON_ID_KEY);
  if (!id) {
    id = "anon_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem(ANON_ID_KEY, id);
  }
  return id;
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [categories, setCategories] = useState({
    necessary: true,    // always on, cannot be toggled off
    analytics: true,
    marketing: false,
  });

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) { setVisible(true); return; }
      const parsed = JSON.parse(raw);
      if (parsed.version !== POLICY_VERSION) { setVisible(true); return; }
      // Already recorded a choice that matches current policy version — hide.
    } catch (_e) {
      setVisible(true);
    }
  }, []);

  const persist = async (choice, cats) => {
    const record = {
      choice,
      categories: cats,
      timestamp: new Date().toISOString(),
      version: POLICY_VERSION,
    };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
    } catch (_e) { /* private-mode etc — non-fatal */ }
    // PHASE 8.42 — Notify any listener (e.g. GAScript) that the consent
    // record changed so they can flip Consent Mode without a page reload.
    try { window.dispatchEvent(new CustomEvent("oodel:consent-updated", { detail: record })); } catch { /* swallow */ }
    // Fire-and-forget audit ping. Don't await — banner closes immediately.
    api.post("/cookies/consent", {
      choice,
      categories: cats,
      anon_id: getAnonId(),
      user_id: null,                  // server fills in if request has auth
      version: POLICY_VERSION,
    }).catch(() => {});
    setVisible(false);
    setShowSettings(false);
  };

  const acceptAll = () => persist("accept_all", ["necessary", "analytics", "marketing"]);
  const rejectAll = () => persist("reject_all", ["necessary"]);
  const savePrefs = () => persist("custom", Object.entries(categories).filter(([_, on]) => on).map(([k]) => k));

  if (!visible) return null;

  return (
    <>
      {/* Slide-up banner — bottom of viewport, full width on mobile, card on desktop. */}
      <div
        className="fixed bottom-0 left-0 right-0 z-[9999] p-4 md:p-6 bg-white border-t-2 border-black shadow-2xl"
        role="dialog"
        aria-modal="false"
        aria-labelledby="cookie-banner-title"
        data-testid="cookie-banner"
      >
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex-1 min-w-0">
            <h2 id="cookie-banner-title" className="font-black text-lg md:text-xl mb-1 tracking-tight">
              🍪 We use cookies
            </h2>
            <p className="text-sm text-neutral-700 leading-snug">
              We use cookies to keep oodel. running smoothly, understand how you use the site, and (with your permission) personalise what you see. Strictly necessary cookies are always on; everything else is your call. See our{" "}
              <a href="/cookie-policy" className="underline hover:text-black">Cookie Policy</a> for the full breakdown.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 shrink-0">
            <button
              type="button"
              onClick={() => setShowSettings(true)}
              className="px-4 py-2 text-sm font-bold border-2 border-black bg-white hover:bg-neutral-100"
              data-testid="cookie-settings-btn"
            >
              Cookie settings
            </button>
            <button
              type="button"
              onClick={rejectAll}
              className="px-4 py-2 text-sm font-bold border-2 border-black bg-white hover:bg-neutral-100"
              data-testid="cookie-reject-btn"
            >
              Reject all
            </button>
            <button
              type="button"
              onClick={acceptAll}
              className="px-4 py-2 text-sm font-bold border-2 border-black bg-black text-white hover:bg-neutral-800"
              data-testid="cookie-accept-btn"
            >
              Accept all
            </button>
          </div>
        </div>
      </div>

      {/* Granular settings modal — only renders when user clicks "Cookie settings" */}
      {showSettings && (
        <div
          className="fixed inset-0 z-[10000] bg-black/60 flex items-end md:items-center justify-center p-0 md:p-4"
          onClick={() => setShowSettings(false)}
          data-testid="cookie-settings-modal"
        >
          <div
            className="bg-white w-full md:max-w-lg max-h-[90vh] overflow-y-auto border-2 border-black"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 md:p-6">
              <h3 className="font-black text-xl mb-1">Cookie settings</h3>
              <p className="text-xs text-neutral-600 mb-5">Choose which categories of cookies you allow. You can change this anytime from the footer.</p>

              <Row
                label="Strictly necessary"
                desc="Keep you signed in, remember your language preference, and keep our site secure. Cannot be disabled — the site won't work without them."
                checked={true}
                disabled={true}
              />
              <Row
                label="Analytics"
                desc="Help us understand which pages and surveys are popular so we can improve oodel. Anonymous; never linked to your identity."
                checked={categories.analytics}
                onChange={(v) => setCategories({ ...categories, analytics: v })}
                testid="cookie-cat-analytics"
              />
              <Row
                label="Marketing & personalisation"
                desc="Show you survey invitations and content that matches your interests. We never sell your data."
                checked={categories.marketing}
                onChange={(v) => setCategories({ ...categories, marketing: v })}
                testid="cookie-cat-marketing"
              />

              <div className="flex justify-end gap-2 mt-6">
                <button
                  type="button"
                  onClick={() => setShowSettings(false)}
                  className="px-4 py-2 text-sm font-bold border-2 border-black bg-white hover:bg-neutral-100"
                  data-testid="cookie-settings-cancel"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={savePrefs}
                  className="px-4 py-2 text-sm font-bold border-2 border-black bg-black text-white hover:bg-neutral-800"
                  data-testid="cookie-settings-save"
                >
                  Save preferences
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Row({ label, desc, checked, disabled = false, onChange, testid }) {
  return (
    <div className="border-b border-neutral-200 py-4 flex items-start justify-between gap-3">
      <div className="flex-1 min-w-0">
        <div className="font-bold text-sm">{label} {disabled && <span className="text-[10px] font-normal text-neutral-500 ml-1">(always on)</span>}</div>
        <div className="text-xs text-neutral-600 mt-1">{desc}</div>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => !disabled && onChange && onChange(!checked)}
        disabled={disabled}
        data-testid={testid}
        className={`relative inline-flex h-6 w-11 shrink-0 items-center border-2 border-black transition-colors ${
          checked ? "bg-black" : "bg-white"
        } ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
      >
        <span
          className={`inline-block h-4 w-4 bg-white border-2 border-black transition-transform ${
            checked ? "translate-x-5 bg-mango" : "translate-x-0.5"
          }`}
        />
      </button>
    </div>
  );
}
